import React, { useState, useEffect } from 'react';
import { Card, Tabs, Input, InputNumber, Select, Button, Spin, message, Space, Upload, Divider, Row, Col, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

const EMPTY_STAT = { icon: '✨', image: '', imageWidth: '', imageHeight: '', label: '', value: '' };
const EMPTY_YEAR = { year: '', status: '', statusClass: 'planned', icon: '✨', image: '', imageWidth: '', imageHeight: '', desc: '', spells: [] };

// ── Small reusable image uploader: uploads immediately, shows preview ───────
// اگر تصویر آپلود شود، به‌جای اموجی روی صفحه نمایش داده می‌شود؛ عرض/ارتفاع هم
// قابل تنظیم است تا سایز تصویر روی صفحه هم کنترل شود — دقیقاً مطابق الگوی
// LandingContentManager.
function ImageField({ label, value, onChange, width, height, onWidthChange, onHeightChange }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await adminAPI.uploadImage(file);
      if (res.data.success) {
        onChange(res.data.path);
        message.success(t('aboutContentManager.uploadSuccess'));
      }
    } catch (err) {
      message.error(err.response?.data?.message || err.message || t('aboutContentManager.uploadError'));
    } finally {
      setUploading(false);
    }
    return false; // جلوگیری از آپلود خودکار antd
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <Space align="start">
        {value && (
          <img
            src={value}
            alt=""
            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #d9d9d9' }}
            onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
          />
        )}
        <Space direction="vertical">
          <Upload beforeUpload={handleUpload} showUploadList={false} accept="image/*">
            <Button icon={<UploadOutlined />} loading={uploading}>
              {value ? t('aboutContentManager.changeImage') : t('aboutContentManager.uploadImage')}
            </Button>
          </Upload>
          <Input
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('aboutContentManager.placeholderUpload')}
            style={{ width: 260 }}
          />
          <Space size={4}>
            <InputNumber
              size="small"
              min={0}
              value={width || undefined}
              onChange={(v) => onWidthChange(v || '')}
              placeholder={t('aboutContentManager.widthPlaceholder')}
              style={{ width: 110 }}
            />
            <InputNumber
              size="small"
              min={0}
              value={height || undefined}
              onChange={(v) => onHeightChange(v || '')}
              placeholder={t('aboutContentManager.heightPlaceholder')}
              style={{ width: 110 }}
            />
          </Space>
        </Space>
      </Space>
    </div>
  );
}

// ── Bullet list editor (array of plain strings) — برای طلسم‌های هر سال ─────
function BulletListEditor({ items, onChange, placeholder }) {
  const { t } = useTranslation();
  const update = (i, v) => {
    const next = items.slice();
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);

  return (
    <div>
      {items.map((item, i) => (
        <Row gutter={8} key={i} style={{ marginBottom: 8 }}>
          <Col flex="auto">
            <Input size="small" value={item} placeholder={placeholder} onChange={(e) => update(i, e.target.value)} />
          </Col>
          <Col flex="28px">
            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />
          </Col>
        </Row>
      ))}
      <Button size="small" icon={<PlusOutlined />} onClick={add}>{t('aboutContentManager.addButton')}</Button>
    </div>
  );
}

// ── Book stats editor (array of { icon, image, imageWidth, imageHeight, label, value }) ──
function BookStatsEditor({ items, onChange, onImageSync }) {
  const { t } = useTranslation();
  const update = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { ...EMPTY_STAT }]);

  return (
    <div>
      {items.map((item, i) => (
        <Card
          size="small"
          key={i}
          style={{ marginBottom: 12 }}
          title={item.label || `آمار ${i + 1}`}
          extra={<Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />}
        >
          <ImageField
            label="تصویر (اختیاری — جایگزین اموجی می‌شود)"
            value={item.image}
            onChange={(v) => { update(i, { image: v }); onImageSync && onImageSync(i, { image: v }); }}
            width={item.imageWidth}
            height={item.imageHeight}
            onWidthChange={(v) => { update(i, { imageWidth: v }); onImageSync && onImageSync(i, { imageWidth: v }); }}
            onHeightChange={(v) => { update(i, { imageHeight: v }); onImageSync && onImageSync(i, { imageHeight: v }); }}
          />
          <Input
            style={{ marginBottom: 8, maxWidth: 140 }}
            value={item.icon}
            onChange={(e) => update(i, { icon: e.target.value })}
            placeholder="اموجی (اگر تصویری نباشد)"
          />
          <Input
            style={{ marginBottom: 8 }}
            value={item.label}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder="برچسب (مثلاً «۷ جلد کتاب»)"
          />
          <Input
            value={item.value}
            onChange={(e) => update(i, { value: e.target.value })}
            placeholder="مقدار (مثلاً «مطالعه‌شده»)"
          />
        </Card>
      ))}
      <Button size="small" icon={<PlusOutlined />} onClick={add}>{t('aboutContentManager.addStat')}</Button>
    </div>
  );
}

// ── Years timeline editor (array of { year, status, statusClass, icon, image, imageWidth, imageHeight, desc, spells[] }) ──
const STATUS_CLASS_OPTIONS = [
  { value: 'active', label: 'فعال (سبز) — در حال توسعه' },
  { value: 'planned', label: 'برنامه‌ریزی‌شده (آبی)' }
];

function YearsEditor({ items, onChange, onImageSync }) {
  const update = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { ...EMPTY_YEAR, spells: [] }]);

  return (
    <div>
      {items.map((item, i) => (
        <Card
          size="small"
          key={i}
          style={{ marginBottom: 12 }}
          title={item.year || `سال ${i + 1}`}
          extra={<Button danger size="small" icon={<DeleteOutlined />} onClick={() => remove(i)} />}
        >
          <ImageField
            label="تصویر (اختیاری — جایگزین اموجی می‌شود)"
            value={item.image}
            onChange={(v) => { update(i, { image: v }); onImageSync && onImageSync(i, { image: v }); }}
            width={item.imageWidth}
            height={item.imageHeight}
            onWidthChange={(v) => { update(i, { imageWidth: v }); onImageSync && onImageSync(i, { imageWidth: v }); }}
            onHeightChange={(v) => { update(i, { imageHeight: v }); onImageSync && onImageSync(i, { imageHeight: v }); }}
          />
          <Input
            style={{ marginBottom: 8, maxWidth: 140 }}
            value={item.icon}
            onChange={(e) => update(i, { icon: e.target.value })}
            placeholder="اموجی (اگر تصویری نباشد)"
          />
          <Row gutter={8} style={{ marginBottom: 8 }}>
            <Col xs={24} sm={12}>
              <Input value={item.year} onChange={(e) => update(i, { year: e.target.value })} placeholder="عنوان سال (مثلاً «سال اول»)" />
            </Col>
            <Col xs={24} sm={12}>
              <Input value={item.status} onChange={(e) => update(i, { status: e.target.value })} placeholder="وضعیت (مثلاً «در حال توسعه»)" />
            </Col>
          </Row>
          <Select
            style={{ width: '100%', marginBottom: 8 }}
            value={item.statusClass}
            options={STATUS_CLASS_OPTIONS}
            onChange={(v) => update(i, { statusClass: v })}
          />
          <TextArea
            rows={2}
            style={{ marginBottom: 8 }}
            value={item.desc}
            onChange={(e) => update(i, { desc: e.target.value })}
            placeholder="توضیح این سال"
          />
          <Divider orientation="left" plain style={{ margin: '8px 0' }}>طلسم‌ها</Divider>
          <BulletListEditor
            items={item.spells || []}
            onChange={(v) => update(i, { spells: v })}
            placeholder="مثلاً وینگاردیوم لویوسا"
          />
        </Card>
      ))}
      <Button size="small" icon={<PlusOutlined />} onClick={add}>افزودن سال</Button>
    </div>
  );
}

export default function AboutContentManager() {
  const { t } = useTranslation();
  const langLabels = { fa: t('common.fa') || 'فارسی', en: t('common.en') || 'English' };
  const [content, setContent] = useState({ fa: {}, en: {} });
  const [activeLang, setActiveLang] = useState('fa');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAboutContent();
      if (res.data.success) {
        const value = res.data.config?.value || { fa: {}, en: {} };
        setContent(value);
      }
    } catch (err) {
      message.error('خطا در بارگذاری محتوای صفحه مراحل بازی');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.upsertAboutContent(content);
      if (res.data.success) {
        message.success('محتوای صفحه مراحل بازی ذخیره شد');
      }
    } catch (err) {
      message.error('خطا در ذخیره محتوا');
    } finally {
      setSaving(false);
    }
  };

  const patchLang = (patch) => {
    setContent((prev) => ({ ...prev, [activeLang]: { ...prev[activeLang], ...patch } }));
  };

  // زبان مقابل زبان فعال — تصاویر همیشه با این زبان همگام می‌شوند
  const otherLang = activeLang === 'fa' ? 'en' : 'fa';

  // همگام‌سازی تصویر یک آیتم داخل آرایه (آمار کتاب/سال‌ها) با همان ایندکس در زبان دیگر؛
  // اگر آرایه‌ی زبان مقابل کوتاه‌تر باشد، آیتم خالی ساخته می‌شود تا فقط تصویر ست شود و متن‌های آن زبان دست‌نخورده بماند.
  const syncArrayItemImage = (arrayKey, defaultItem) => (index, patch) => {
    setContent((prev) => {
      const otherArr = (prev[otherLang]?.[arrayKey] || []).slice();
      while (otherArr.length <= index) otherArr.push({ ...defaultItem });
      otherArr[index] = { ...otherArr[index], ...patch };
      return { ...prev, [otherLang]: { ...prev[otherLang], [arrayKey]: otherArr } };
    });
  };

  // کپی یک‌بارهٔ همهٔ تصاویر موجود در فارسی به انگلیسی (برای همگام کردن وضعیت فعلی)
  const backfillImagesFromFa = () => {
    setContent((prev) => {
      const fa = prev.fa || {};
      const en = { ...(prev.en || {}) };

      const mergeArrayImages = (key, defaultItem) => {
        const faArr = fa[key] || [];
        if (!faArr.length) return;
        const enArr = (en[key] || []).slice();
        faArr.forEach((item, i) => {
          if (!item.image) return;
          while (enArr.length <= i) enArr.push({ ...defaultItem });
          enArr[i] = { ...enArr[i], image: item.image, imageWidth: item.imageWidth, imageHeight: item.imageHeight };
        });
        en[key] = enArr;
      };

      mergeArrayImages('bookStats', EMPTY_STAT);
      mergeArrayImages('years', EMPTY_YEAR);

      return { ...prev, en };
    });
    message.success('تصاویر فارسی روی انگلیسی هم کپی شد. برای ذخیره‌ی دائمی، دکمه «ذخیره همه تغییرات» را بزنید.');
  };

  if (loading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;

  const c = content[activeLang] || {};

  return (
    <div style={{ maxWidth: 900 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="متن‌ها و تصاویر صفحه «مراحل بازی و داستان هاگوارتز» (About)"
        description="این بخش‌ها مستقیماً روی صفحه‌ی /about کاربران فارسی و انگلیسی نمایش داده می‌شود. متن‌ها جدا برای هر زبان ذخیره می‌شوند، اما تصاویر بین دو زبان مشترک‌اند: هر تصویری که در یکی از تب‌های فارسی/انگلیسی آپلود یا تغییر کند، خودکار در آن یکی هم ست می‌شود. برای هر آمار کتاب یا سال، یا اموجی بگذارید یا تصویر آپلود کنید — اگر تصویر آپلود شود، جایگزین اموجی می‌شود و سایزش هم از همین‌جا قابل تنظیم است."
      />

      <Tabs
        activeKey={activeLang}
        onChange={setActiveLang}
        items={['fa', 'en'].map((lng) => ({ key: lng, label: langLabels[lng] }))}
      />

      <Card title="عنوان صفحه" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان اصلی" value={c.title} onChange={(e) => patchLang({ title: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="زیرعنوان" value={c.subtitle} onChange={(e) => patchLang({ subtitle: e.target.value })} />
      </Card>

      <Card title="مقدمه (چرا این پروژه ساخته شد؟)" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان بخش" value={c.introTitle} onChange={(e) => patchLang({ introTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={5} placeholder="متن مقدمه (هر پاراگراف در یک خط جدا)" value={c.introText} onChange={(e) => patchLang({ introText: e.target.value })} />
      </Card>

      <Card title="آمار کتاب‌ها" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان بخش" value={c.bookTitle} onChange={(e) => patchLang({ bookTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح بخش" value={c.bookText} onChange={(e) => patchLang({ bookText: e.target.value })} style={{ marginBottom: 12 }} />
        <Divider orientation="left" plain>کارت‌های آمار</Divider>
        <BookStatsEditor
          items={c.bookStats || []}
          onChange={(v) => patchLang({ bookStats: v })}
          onImageSync={syncArrayItemImage('bookStats', EMPTY_STAT)}
        />
      </Card>

      <Card title="مراحل هفت‌ساله (Timeline)" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان بخش" value={c.yearsTitle} onChange={(e) => patchLang({ yearsTitle: e.target.value })} style={{ marginBottom: 12 }} />
        <Divider orientation="left" plain>سال‌ها</Divider>
        <YearsEditor
          items={c.years || []}
          onChange={(v) => patchLang({ years: v })}
          onImageSync={syncArrayItemImage('years', EMPTY_YEAR)}
        />
      </Card>

      <Card title="درخواست ویژگی جدید" style={{ marginBottom: 16 }}>
        <Input addonBefore="عنوان بخش" value={c.requestTitle} onChange={(e) => patchLang({ requestTitle: e.target.value })} style={{ marginBottom: 8 }} />
        <TextArea rows={2} placeholder="توضیح بخش" value={c.requestDesc} onChange={(e) => patchLang({ requestDesc: e.target.value })} />
      </Card>

      <Space style={{ marginBottom: 40 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>بازخوانی</Button>
        <Button icon={<UploadOutlined />} onClick={backfillImagesFromFa}>کپی تصاویر فارسی روی انگلیسی</Button>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>ذخیره همه تغییرات</Button>
      </Space>
    </div>
  );
}
